import { ClinicAuthError } from "@auth/clinicAuthClient";
import { useSessionKey } from "@auth/sessionStore";
import {
  isKnownCallStatus,
  useIncomingCallStore,
} from "@notifications/incomingCallStore";
import {
  subscribeToFrames,
  subscribeToReconnect,
} from "@notifications/notificationFrames";
import {
  fetchPhoneCall,
  isTerminalPhoneCallStatus,
  type PhoneCall,
  toDisclosureState,
  toHoldState,
} from "@phone/phoneCallsClient";
import { useEffect } from "react";

const cardFor = (callUuid: string) =>
  useIncomingCallStore.getState().calls.find((c) => c.callUuid === callUuid);

/** A live card re-reads its row this often, for an end its socket never delivered. */
const ENDED_POLL_MS = 15_000;

const MAX_CONSECUTIVE_FETCH_FAILURES = 3;

const SETTLED_SLIP_MS = 12_000;

/**
 * The "ended" frame is authoritative and settles or closes the card itself; the
 * row read only adds the disposition, which no frame carries. Frames are never
 * replayed, so a direct `GET /phone-calls/{uuid}` on poll and on reconnect is
 * the backstop for a missed end, ordered by `isFresherPhoneCall`.
 */
export const useIncomingCalls = () => {
  const userUuid = useSessionKey();
  const show = useIncomingCallStore((s) => s.show);
  const reconcileRow = useIncomingCallStore((s) => s.reconcileRow);
  const markUnreachable = useIncomingCallStore((s) => s.markUnreachable);
  const settle = useIncomingCallStore((s) => s.settle);
  const close = useIncomingCallStore((s) => s.close);
  const withdraw = useIncomingCallStore((s) => s.withdraw);

  useEffect(() => {
    if (!userUuid) return;

    let disposed = false;
    const settledTimers = new Map<string, number>();
    const failures = new Map<string, number>();
    // 404'd rows: polled no further, but never declared over on that alone.
    const abandoned = new Set<string>();

    // Neither the row's enrichment nor a second end restarts the dismissal timer.
    const settleOutbound = (
      callUuid: string,
      version: Pick<PhoneCall, "revision" | "updatedAt">,
      row?: PhoneCall,
    ) => {
      if (row) reconcileRow(row);
      settle(
        callUuid,
        row
          ? {
              rowRead: true,
              disposition: row.disposition,
              outcome: row.outcome,
            }
          : { rowRead: false },
        version,
      );
      if (!cardFor(callUuid)?.settled || settledTimers.has(callUuid)) return;
      settledTimers.set(
        callUuid,
        window.setTimeout(() => {
          settledTimers.delete(callUuid);
          close(callUuid);
        }, SETTLED_SLIP_MS),
      );
    };

    const reconcile = async (callUuid: string): Promise<void> => {
      let call: PhoneCall;
      try {
        call = await fetchPhoneCall(callUuid);
      } catch (error) {
        // A failed read is not evidence the call ended: only a terminal row or frame may say so.
        if (disposed) return;
        if (error instanceof ClinicAuthError && error.status === 404) {
          abandoned.add(callUuid);
          markUnreachable(callUuid);
          return;
        }
        const count = (failures.get(callUuid) ?? 0) + 1;
        failures.set(callUuid, count);
        if (count >= MAX_CONSECUTIVE_FETCH_FAILURES) markUnreachable(callUuid);
        return;
      }
      if (disposed) return;
      failures.delete(callUuid);
      abandoned.delete(callUuid);
      const card = cardFor(callUuid);
      if (!card) return;
      if (isTerminalPhoneCallStatus(call.status)) {
        if (card.origin === "staff_outbound") {
          settleOutbound(callUuid, call, call);
        } else {
          close(callUuid);
        }
        return;
      }
      if (card.settled) return;
      if (!isKnownCallStatus(call.status)) {
        withdraw(callUuid);
        return;
      }
      reconcileRow(call);
    };

    const poll = window.setInterval(() => {
      for (const card of useIncomingCallStore.getState().calls) {
        if (card.settled || abandoned.has(card.callUuid)) continue;
        void reconcile(card.callUuid);
      }
    }, ENDED_POLL_MS);

    const unsubscribeReconnect = subscribeToReconnect(() => {
      abandoned.clear();
      for (const card of useIncomingCallStore.getState().calls) {
        if (card.settled) continue;
        void reconcile(card.callUuid);
      }
    });

    const unsubscribe = subscribeToFrames((events) => {
      for (const event of events) {
        if (event.type !== "phone_call" || !event.uuid) continue;
        if (!isKnownCallStatus(event.status)) {
          withdraw(event.uuid);
          continue;
        }
        // `continue` keeps the card down: brian emits further "ended" frames while filing the call.
        if (isTerminalPhoneCallStatus(event.status)) {
          const card = cardFor(event.uuid);
          if (card?.origin === "staff_outbound") {
            settleOutbound(event.uuid, {
              revision: event.revision ?? null,
              updatedAt: null,
            });
            if (!card.settled?.rowRead) void reconcile(event.uuid);
          } else {
            close(event.uuid);
          }
          continue;
        }
        const before = cardFor(event.uuid);
        show(
          event.uuid,
          event.label ?? null,
          event.status,
          event.revision ?? null,
          null,
          toDisclosureState(event.disclosure_state),
          {
            holdState: toHoldState(event.hold_state),
            heldAt: event.held_at ?? null,
          },
        );
        const after = cardFor(event.uuid);
        // Read when a card goes up, on an outbound `bridged` (the row first carries
        // `started_at`), and on a hold ending unbridged (only the row carries `handoff_failed`).
        if (
          after &&
          (before === undefined ||
            (after.origin === "staff_outbound" &&
              event.status === "bridged" &&
              before.status !== "bridged") ||
            (before.holdState !== "none" &&
              after.holdState === "none" &&
              after.status !== "bridged"))
        ) {
          void reconcile(event.uuid);
        }
      }
    });

    return () => {
      disposed = true;
      window.clearInterval(poll);
      for (const timer of settledTimers.values()) window.clearTimeout(timer);
      unsubscribeReconnect();
      unsubscribe();
    };
  }, [userUuid, show, reconcileRow, markUnreachable, settle, close, withdraw]);
};
